/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import { renderHighlightedText } from '../utils/renderHighlightedText';
import { useEffect, useRef, useState } from 'react';

export const useSearchHighlightText = (data: any[]) => {
  const [term, setTerm] = useState('');
  const [matches, setMatches] = useState<HTMLElement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const refs = useRef<HTMLElement[]>([]);
  const matchCounterRef = useRef(0);

  const beginHighlightPass = () => {
    refs.current = [];
    matchCounterRef.current = 0;
  };

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setTerm(e.target.value);
    setCurrentIndex(0);
  }

  function handleClearSearch() {
    setTerm('');
    setCurrentIndex(0);
  }

  function prevMatch() {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }

  function nextMatch() {
    setCurrentIndex((next) => Math.min(next + 1, matches.length - 1));
  }

  const getHighlightedNodes = (text: string): React.ReactNode[] => {
    return renderHighlightedText(
      text,
      term,
      refs,
      currentIndex,
      () => matchCounterRef.current++
    );
  };

  // Track mathces
  useEffect(() => {
    const next = refs.current.filter(Boolean);
    setMatches(next);
  }, [data.length, term, currentIndex]);

  // Scroll active match
  useEffect(() => {
    refs.current[currentIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [currentIndex]);

  return {
    term,
    setTerm: handleSearchChange,
    handleClearSearch,
    prevMatch,
    nextMatch,
    matches,
    currentIndex,
    beginHighlightPass,
    getHighlightedNodes,
  };
};
